/**
 * Pure helpers for electrical raceway/conduit rendering (2D plan + 3D viewer):
 * the opt-in `Conduit` / `PullBox` API rows → the flat geometry the viewer
 * layer draws (a 3D polyline per conduit at its world `z_m`, box glyph
 * positions per pull box), kind-based color coding, and a trade-size tag.
 *
 * The engine emits a storey-LOCAL `z_m` (measured from each storey's floor) and
 * the route's `storey_id`; the viewer stacks storeys at their world elevations
 * (ducts/devices arrive world-lifted from the DB rows). So these helpers lift
 * the conduit/box z by the storey elevation — exactly the server's
 * `lift_z_to_world` — so raceways align with the ducts/mesh instead of
 * collapsing every storey to ground level.
 *
 * This is a THIN adapter — the engine already routed + sized every raceway;
 * these functions only reshape its Output for drawing. Deterministic, no IO,
 * no three.js dependency, so it unit-tests as plain functions (mirrors
 * ../mep/ducts).
 */
import type { Conduit, PullBox } from "../api/types";
import { planToThree, type Vec3 } from "./ducts";
import { flipYPoints } from "../util/coordinate";

/**
 * Raceway kind → stroke/material color. EMT/IMC/RMC are the common steel
 * conduits (cool grey-blues), PVC is warmer, cable tray is distinct green so
 * the operator can tell a tray run apart at a glance. Unknown kinds fall back
 * to a neutral grey.
 */
export const RACEWAY_COLORS: Record<string, string> = {
  emt: "#7db3d8",
  imc: "#5a8fb5",
  rmc: "#3f6e91",
  pvc_sch40: "#c98f5a",
  cable_tray: "#7ac07a",
};

const RACEWAY_FALLBACK_COLOR = "#aab2c0";

export function racewayColor(kind: string): string {
  return RACEWAY_COLORS[kind] ?? RACEWAY_FALLBACK_COLOR;
}

/** `3/4" EMT` style tag for the plan; falls back to the raw kind token. */
export function conduitSizeLabel(
  conduit: Pick<Conduit, "kind" | "trade_size_in">,
): string {
  const kindLabel = conduit.kind.replace(/_/g, " ").toUpperCase();
  return `${conduit.trade_size_in.toFixed(2)}" ${kindLabel}`;
}

/**
 * What the layer needs per conduit: the DTO reshaped for drawing plus its
 * server storey id (the route's storey), the basis for storey filtering.
 */
export interface LayerConduit {
  id: string;
  kind: string;
  trade_size_in: number;
  fill_pct: number;
  /** Centerline in plan meters with the world `z_m` applied to every vertex. */
  centerline: Vec3[];
  /** Server storey id of the conduit's route. */
  storey_id: string;
}

/** What the layer needs per pull box (a point glyph). */
export interface LayerPullBox {
  id: string;
  x_m: number;
  y_m: number;
  z_m: number;
  sizing_basis: string;
  /** 314.28 linear min dimension (inches); 0 for a 314.16 box. */
  min_dim_in: number;
  /** 314.16 box volume (cubic inches); undefined for a 314.28 box. */
  box_volume_in3?: number;
  storey_id: string;
}

/**
 * API `Conduit` → {@link LayerConduit}: lift the 2D `[x, y]` polyline to a
 * plan Vec3 by applying the conduit's world `z_m` (storey-local `z_m` +
 * `elevation`) to every vertex, so the 3D layer draws the run at its real
 * stacked height. `elevation` is the conduit's storey world elevation (0 for
 * single-storey / when unknown).
 */
export function conduitToLayer(conduit: Conduit, elevation = 0): LayerConduit {
  const worldZ = conduit.z_m + elevation;
  return {
    id: conduit.id,
    kind: conduit.kind,
    trade_size_in: conduit.trade_size_in,
    fill_pct: conduit.fill_pct,
    centerline: conduit.polyline.map(([x, y]) => [x, y, worldZ] as Vec3),
    storey_id: conduit.storey_id,
  };
}

/**
 * API `PullBox` → {@link LayerPullBox}: unpack the `[x, y]` position and lift
 * `z_m` to world by the storey `elevation` (0 for single-storey / unknown).
 */
export function pullBoxToLayer(box: PullBox, elevation = 0): LayerPullBox {
  return {
    id: box.id,
    x_m: box.position[0],
    y_m: box.position[1],
    z_m: box.z_m + elevation,
    sizing_basis: box.sizing_basis,
    min_dim_in: box.min_dim_in,
    box_volume_in3: box.box_volume_in3,
    storey_id: box.storey_id,
  };
}

/**
 * Flat list of straight line segments (plan coords) for one conduit
 * centerline, `[[start, end], ...]` — the geometry the 2D plan and the 3D
 * `LineSegments` both consume. Zero-length spans are dropped.
 */
export function conduitLineSegments(centerline: readonly Vec3[]): [Vec3, Vec3][] {
  const segments: [Vec3, Vec3][] = [];
  for (let i = 0; i + 1 < centerline.length; i += 1) {
    const a = centerline[i];
    const b = centerline[i + 1];
    if (a[0] === b[0] && a[1] === b[1] && a[2] === b[2]) continue;
    segments.push([[...a] as Vec3, [...b] as Vec3]);
  }
  return segments;
}

/** Flat three.js vertex triples for a conduit's line segments (x, z, y). */
export function conduitLineVertices(centerline: readonly Vec3[]): number[] {
  const verts: number[] = [];
  for (const [a, b] of conduitLineSegments(centerline)) {
    verts.push(...planToThree(a), ...planToThree(b));
  }
  return verts;
}

/** SVG `points` for the plan polyline (same y-flip as ducts/wizard). */
export function conduitPolylinePoints(centerline: readonly Vec3[]): string {
  return flipYPoints(centerline);
}
